export const leadStatuses = [
  'unsent',
  'sent',
  'failed',
  'positive_reply',
  'negative_reply',
  'junk',
  'hard_bounce'
] as const;

export type LeadStatus = (typeof leadStatuses)[number];
export type DeliveryStatus = 'not_sent' | 'sent' | 'failed';
export type ReplyStatus = 'none' | 'positive' | 'negative';
export type SuppressionStatus = 'available' | 'suppressed';

export const leadDataTypes = ['cpa', 'retail', 'va', 'other'] as const;

export type LeadDataType = (typeof leadDataTypes)[number];

export const dataTypeLabels: Record<LeadDataType, string> = {
  cpa: 'CPA',
  retail: 'Retail',
  va: 'VA',
  other: 'Other'
};

export const statusLabels: Record<LeadStatus, string> = {
  unsent: 'Unsent',
  sent: 'Sent',
  failed: 'Failed',
  positive_reply: 'Positive Reply',
  negative_reply: 'Negative Reply',
  junk: 'Junk',
  hard_bounce: 'Hard Bounce'
};

export interface Lead {
  id: string;
  email: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  website: string | null;
  linkedinUrl: string | null;
  company: string | null;
  title: string | null;
  country: string | null;
  city: string | null;
  state: string | null;
  street: string | null;
  postalCode: string | null;
  timeZone: string | null;
  employeeSize: string | null;
  industry: string | null;
  dataType: LeadDataType;
  uploadDate: string;
  customFields: Record<string, unknown>;
  status: LeadStatus;
  deliveryStatus: DeliveryStatus;
  replyStatus: ReplyStatus;
  suppressionStatus: SuppressionStatus;
  sentDate: string | null;
  campaignId: string | null;
  sourceJobId: string | null;
  sourceFile: string | null;
  sourceSheet: string | null;
  suppressedAt: string | null;
  suppressionReason: LeadStatus | null;
  createdAt: string;
  updatedAt: string;
}

export type LeadDetailsUpdate = Pick<
  Lead,
  | 'email'
  | 'fullName'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'website'
  | 'linkedinUrl'
  | 'company'
  | 'title'
  | 'country'
  | 'city'
  | 'state'
  | 'street'
  | 'postalCode'
  | 'timeZone'
  | 'employeeSize'
  | 'industry'
  | 'dataType'
  | 'uploadDate'
  | 'customFields'
  | 'status'
  | 'sentDate'
  | 'campaignId'
>;

export interface LeadFilters {
  reviewOnly?: boolean;
  websiteUpdated?: string;
  websiteUpdatedFrom?: string;
  websiteUpdatedTo?: string;
  q?: string;
  emailContains?: string;
  status?: LeadStatus | '';
  campaignId?: string;
  sentDateFrom?: string;
  sentDateTo?: string;
  uploadDateFrom?: string;
  uploadDateTo?: string;
  country?: string;
  industry?: string;
  industryMissing?: boolean;
  timeZone?: string;
  provider?: 'google' | 'microsoft' | 'private' | '';
  emailType?: 'generic' | 'other' | '';
  enrichment?: 'saved' | 'failed' | 'pending' | 'full' | 'partial' | 'no_data' | 'legacy' | '';
  dataType?: LeadDataType | '';
  preset?: 'fresh' | 'unsent' | '';
  sourceJobId?: string;
  sourceSheet?: string;
  sourceFile?: string;
  sourceUploadDate?: string;
  sourceOwnerId?: string;
  sourceKind?: 'file' | 'paste' | 'manual';
  sourceContains?: string;
  limit?: number;
  sortBy?: 'lead' | 'company' | 'location' | 'industry' | 'dataType' | 'source' | 'status' | 'sentDate' | 'uploaded';
  sortDir?: 'asc' | 'desc';
}

export interface DataSource {
  countsPending?: boolean;
  countsError?: boolean;
  sourceJobId: string | null;
  sourceOwnerId: string;
  sourceFile: string;
  uploadDate: string;
  createdAt: string;
  updatedAt: string;
  kind: 'file' | 'paste' | 'manual';
  originalRows: number;
  leadCount: number;
  sheetCount: number;
  insertedRows: number;
  importUpdatedRows: number;
  duplicateRows: number;
  invalidRows: number;
  importSuppressedRows: number;
  unsentCount: number;
  sentCount: number;
  positiveCount: number;
  negativeCount: number;
  failedCount: number;
  junkCount: number;
  hardBounceCount: number;
  suppressedCount: number;
}

export interface DataSourcePage {
  rows: DataSource[];
  nextCursor: string | null;
}

export interface DataSourceOutcomeSummary {
  leadCount: number;
  unsentCount: number;
  sentCount: number;
  positiveCount: number;
  negativeCount: number;
  failedCount: number;
  junkCount: number;
  hardBounceCount: number;
  suppressedCount: number;
  updatedAt: string | null;
}

export type FollowUpTaskStatus = 'pending' | 'completed' | 'dismissed';

export interface FollowUpTask {
  id: string;
  sourceKey: string;
  sourceLabel: string;
  sourceJobId: string | null;
  sentDate: string;
  sequence: 1 | 2;
  dueDate: string;
  status: FollowUpTaskStatus;
  completedAt: string | null;
  createdAt: string;
}

export interface FollowUpSummary {
  dueToday: number;
  overdue: number;
  upcoming: number;
}

export interface MailLogSource {
  id: string;
  ownerUserId: string;
  name: string;
  portalUrl: string;
  enabled: boolean;
  allowInsecureTls: boolean;
  intervalMinutes: number;
  lookbackDays: number;
  lastSyncAt: string | null;
  lastSyncStatus: 'running' | 'completed' | 'failed' | null;
  lastSyncMessage: string | null;
  syncTotalFiles: number;
  syncProcessedFiles: number;
  syncUpdatedRows: number;
  syncCurrentFile: string | null;
  syncStartedAt: string | null;
  createdAt: string;
}

export interface LeadPage {
  rows: Lead[];
  nextCursor: string | null;
  totalCount: number | null;
}

export interface LeadTimeline {
  statusEvents: Array<{
    oldStatus: LeadStatus | null;
    newStatus: LeadStatus;
    source: string;
    createdAt: string;
  }>;
  sourceMemberships: Array<{
    jobId: string | null;
    sourceFile: string;
    sourceKind: 'file' | 'paste' | 'manual' | 'current';
    uploadDate: string | null;
    sourceSheets: string[];
    totalRows: number;
    uniqueRows: number;
    jobStatus: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  }>;
  auditEvents: Array<{
    action: string;
    userEmail: string | null;
    userName: string | null;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
}

export interface RecoverableStatusChange {
  eventId: string;
  leadId: string;
  email: string;
  fullName: string | null;
  oldStatus: LeadStatus;
  newStatus: LeadStatus;
  changedAt: string;
}

export interface StatusUpdateBatch {
  id: string;
  fileName: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  totalRows: number;
  validRows: number;
  invalidRows: number;
  duplicateRows: number;
  insertedRows: number;
  updatedRows: number;
  protectedRows: number;
  unchangedRows: number;
  unmatchedRows: number;
  processedRows: number;
  createdAt: string;
  completedAt: string | null;
}

export interface StatusUpdateBatchSource {
  sourceJobId: string | null;
  sourceFile: string;
  sourceSheet: string | null;
  totalRows: number;
  updatedRows: number;
  protectedRows: number;
  insertedRows: number;
  unmatchedRows: number;
}

export interface StatusUpdateBatchDetail extends StatusUpdateBatch {
  sources: StatusUpdateBatchSource[];
}

export interface Job {
  id: string;
  jobType: 'lead_import' | 'deduplication' | 'status_update' | 'source_enrichment' | 'lead_export' | 'bulk_field_update' | 'mail_log_sync';
  status: 'queued' | 'running' | 'paused' | 'stuck' | 'completed' | 'failed';
  fileName: string;
  totalRows: number;
  insertedRows: number;
  updatedRows: number;
  duplicateRows: number;
  invalidRows: number;
  suppressedRows: number;
  cleanRows: number;
  exportUrl: string | null;
  rejectedReportUrl: string | null;
  progressPercent: number;
  options: Record<string, unknown>;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  updatedAt?: string | null;
  completedAt: string | null;
}

export interface EnrichmentWorkspaceSummary {
  fieldCoverage?: Record<string,number>;
  fullyEnrichedLeads: number;
  partialLeads: number;
  noDataLeads: number;
  legacyScannedLeads: number;
  totalLeads: number;
  enrichedLeads: number;
  failedLeads: number;
  remainingLeads: number;
  activeJobs: number;
  queuedJobs: number;
  calculatedAt: string;
}

export interface DashboardStats {
  total: number;
  suppressed: number;
  byStatus: Record<LeadStatus, number>;
  byDeliveryStatus: Record<DeliveryStatus, number>;
  byReplyStatus: Record<ReplyStatus, number>;
  bySuppressionStatus: Record<SuppressionStatus, number>;
  recentJobs: Job[];
}

export interface PerformanceSnapshot {
  slowThresholdMs: number;
  capturedAt: string;
  recentSlowRequests: Array<{
    method: string;
    path: string;
    statusCode: number;
    durationMs: number;
    createdAt: string;
  }>;
  routes: Array<{
    path: string;
    calls: number;
    averageMs: number;
    maxMs: number;
    slowCalls: number;
  }>;
}

export interface PasteImportResult {
  firstEmail?: string | null;
  totalRows: number;
  insertedRows: number;
  updatedRows: number;
  duplicateRows: number;
  invalidRows: number;
  suppressedRows: number;
}

export interface ImportConflictSummary {
  totalIncoming: number;
  validRows: number;
  uniqueEmails: number;
  duplicateEmails: number;
  invalidBlocks: number;
  newEmails: number;
  existingEmails: number;
  protectedPositive: number;
  protectedNegative: number;
  protectedFailed: number;
  suppressed: number;
}

export interface PastePreviewResult {
  rows: Array<Pick<Lead, 'email' | 'fullName' | 'phone' | 'website' | 'company' | 'title' |
    'linkedinUrl' | 'country' | 'city' | 'state' | 'street' | 'postalCode' | 'timeZone' |
    'employeeSize' | 'industry' | 'dataType' | 'uploadDate' | 'status' | 'sentDate' | 'customFields'>>;
  totalRows: number;
  invalidBlocks: number;
  issues: Array<{ line: number; message: string }>;
  conflictSummary?: ImportConflictSummary;
}

export type ExtractedLeadDraft = PastePreviewResult['rows'][number] & {
  firstName?: string | null;
  lastName?: string | null;
  campaignId?: string | null;
};

export interface MediaExtractionPreview extends PastePreviewResult {
  rows: ExtractedLeadDraft[];
  engine: string;
  warnings: string[];
  extractedTextPreview: string;
}

export type UserRole = 'admin' | 'manager' | 'uploader' | 'viewer';

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  workspaceName: string;
  role: UserRole;
  forcePasswordChange: boolean;
}

export interface AppUser extends CurrentUser {
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'paused' | 'archived';
  leadCount: number;
  sentCount: number;
  positiveCount: number;
  suppressedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AnalyticsPayload {
  campaignPerformance: Array<{
    id: string;
    name: string;
    leadCount: number;
    sentCount: number;
    failedCount: number;
    positiveCount: number;
    negativeCount: number;
    suppressedCount: number;
  }>;
  dailyLeadActivity: Array<{
    day: string;
    createdCount: number;
  }>;
  dailyJobActivity: Array<{
    day: string;
    jobCount: number;
    completedCount: number;
    failedCount: number;
  }>;
}

export interface SuppressedEmail {
  emailNorm: string;
  email: string;
  reason: LeadStatus;
  source: string;
  leadId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown>;
  ipAddress: string | null;
  userEmail: string | null;
  userName: string | null;
  createdAt: string;
}
